import React, { useState } from 'react';
import { getIntl } from '../../../../../utils/intl';
import { useHasPermission, useIsProfileOwner } from '../../../../v2BaseComponents/Permissions/hooks';
import { Dynamo, MedicalDataTypes } from '../../../../../primary-record-types';
import { useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useResource from '../../../../../hooks/api/useResource';
import { categoryToDomain } from '../../../../../utils/resourceUtils/medicalDataPermissions';
import DetailsList from './DetailsList';
import FullScreenImageView from '../../../../v2BaseComponents/FullScreenImageView/FullScreenImageView';
import urls from '../../../../../utils/urls';
import { getCategoryLabel } from '../../../../../utils/resourceUtils/messages';
import HistoryList from './HistoryList';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import LargeDescriptiveImage from '../../../../v2BaseComponents/LargeDescriptiveImage';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import ClinicalImpressions from './ClinicalImpressions';
import DeleteVisitSeriesPrompt from './DeleteVisitSeriesPrompt';
import { AvatarAPIImagesProvider } from '../../../../v2BaseComponents/FullScreenImageView';
import { AvatarAPIViewerImage } from '../../../../v2BaseComponents/FullScreenImageView/AvatarAPIImagesProvider';

const intl = getIntl('resource-details-tab', {
    edit: 'Edit',
    delete: 'Delete',
    describe: 'Describe',
});

export type DetailsTabOwnProps = {
    profileId: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    resourceId: string;
    setIsOpen: (isOpen: boolean) => void;
};

export type DetailsTabProps = DetailsTabOwnProps;

const DetailsTab: React.FC<DetailsTabProps> = ({ profileId, category, resourceId, setIsOpen }) => {
    const recordEvent = useRecordEvent();
    const goBack = useGoBack();
    const { resource, isLoadingResource, deleteResource, isDeletingResource } = useResource({
        medicalProfileId: profileId,
        resourceCategory: category,
        resourceId,
        onDeleted: () => {
            goBack();
        },
    });

    const isOwner = useIsProfileOwner(profileId);
    const navigator = useProfilesTabNavigation();
    const imageEntries = (resource?.images || []).map<AvatarAPIViewerImage<Dynamo.ResourceImage>>((image) => ({
        imageViewImageUrl: urls.api.medicalData.resourceImage(profileId, category, resourceId, image.resourceImageId),
        imageViewImageDescription: image.description,
        ...image,
    }));

    const hasSourceCategoryEditPermissions = useHasPermission(profileId, categoryToDomain(category), 'edit');
    const canEditSourceResource = isOwner || hasSourceCategoryEditPermissions;
    const [isSeriesDeletePromptOpen, setIsSeriesDeletePromptOpen] = useState(false);
    const visitInSeries =
        resource?.resource.type === 'visit' && resource.resource.recurrenceGroupId ? resource.resource : null;
    return (
        <AnimateLoadedContent isLoading={isLoadingResource}>
            {() => (
                <ActionButtonLayout
                    whiteBackground
                    scrollable
                    actions={[
                        {
                            title: intl('describe'),
                            isDisabled: isDeletingResource,
                            icon: 'Info',
                            onPress: () => {
                                setIsOpen(true);
                            },
                            testID: 'describe-resource-button',
                        },
                        {
                            title: intl('edit'),
                            isDisabled: isDeletingResource,
                            icon: 'Edit2',
                            onPress: () => {
                                navigator.navigate('edit-data', {
                                    profileId,
                                    category,
                                    resourceId,
                                });
                            },
                            testID: 'edit-resource-button',
                            show: canEditSourceResource,
                        },
                        {
                            title: intl('delete', { category: getCategoryLabel(category) }),
                            isDisabled: isDeletingResource,
                            icon: 'Trash2',
                            onPress: () => {
                                recordEvent(`delete${getCategoryLabel(category)}`);
                                if (visitInSeries) {
                                    setIsSeriesDeletePromptOpen(true);
                                    return;
                                }
                                deleteResource(resourceId);
                            },
                            testID: 'delete-resource-button',
                            show: canEditSourceResource,
                        },
                    ]}
                >
                    <MainContentContainer>
                        {visitInSeries ? (
                            <DeleteVisitSeriesPrompt
                                isOpen={isSeriesDeletePromptOpen}
                                setIsOpen={setIsSeriesDeletePromptOpen}
                                profileId={profileId}
                                visit={visitInSeries}
                                onDeleteSingle={() => deleteResource(resourceId)}
                                onSeriesDeleted={goBack}
                            />
                        ) : null}
                        <ContentStack>
                            {resource ? <DetailsList resource={resource} /> : null}
                            <AvatarAPIImagesProvider images={imageEntries}>
                                {(renderImagesData) => (
                                    <FullScreenImageView
                                        renderImagesData={renderImagesData}
                                        renderImages={(images, onPress, getIsPressEnabled) => (
                                            <Stack vertical fullWidth space={2}>
                                                {images.map((image) => (
                                                    <LargeDescriptiveImage
                                                        testID={`resource-details-image`}
                                                        key={image.imageId}
                                                        imageUrl={image.imageViewImageUrl}
                                                        description={image.imageViewImageDescription}
                                                        onPress={
                                                            getIsPressEnabled(image) ? () => onPress(image) : undefined
                                                        }
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    />
                                )}
                            </AvatarAPIImagesProvider>
                            {resource ? <ClinicalImpressions resource={resource} /> : null}
                            {resource ? <HistoryList resource={resource} medicalProfileId={profileId} /> : null}
                        </ContentStack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(DetailsTab);
